import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AuthStore } from '../stores/auth/auth.store';
import { ClientStore } from '../stores/client/client.store';
import { PlatformUtil } from '../utils/platform/platform.util';
import { SmartrMsalService } from './auth/smartr-msal.service';
import { ClientService } from './client/client.service';

/**
 * Service responsible for initialising app state on page load/refresh
 * Restores client and auth state from MSAL data
 */
@Injectable({
  providedIn: 'root',
})
export class AppInitialisationService {
  private readonly clientStore = inject(ClientStore);
  private readonly authStore = inject(AuthStore);
  private readonly smartrMsalService = inject(SmartrMsalService);
  private readonly clientService = inject(ClientService);
  private _hydratingProfile = false;

  async initialise(): Promise<void> {
    try {
      await this.ensureWebSessionRestored();

      if (this.smartrMsalService.isAuthenticated()) {
        await this.handleAuthenticatedInitialisation();
      }
    } catch (error) {
      console.error('Error during app initialisation', error);
    }
  }

  /**
   * Attempt a silent MSAL session restoration on the web platform when the user
   * is currently unauthenticated (no cached account) – skips on native because
   * iframe-based silent auth is unreliable/slower there.
   */
  private async ensureWebSessionRestored(): Promise<void> {
    if (!this.smartrMsalService.isAuthenticated() && PlatformUtil.isWeb()) {
      await this.smartrMsalService.tryRestoreSession();
    }
  }

  /**
   * Handles authenticated post-restore initialisation flow
   */
  private async handleAuthenticatedInitialisation(): Promise<void> {
    const client = this.clientStore.client();
    if (client) {
      await this.getClientProfile(client.clientId);

      if (!this.authStore.hasInitialised()) {
        this.authStore.setHasInitialised(true);
      }
    }

    if (!client && !this.authStore.hasInitialised()) {
      setTimeout(() => {
        if (!this.authStore.hasInitialised()) {
          this.authStore.setHasInitialised(true);
        }
      }, 1500);
    }
  }


  /**
   * Fetches the client profile data (personal details & address history) if not already loaded.
   * @param clientId The ID of the client whose profile is to be fetched.
   * @returns A promise that resolves when the profile data is fetched.
   */
  private async getClientProfile(clientId: number): Promise<void> {
    if (!clientId || this._hydratingProfile) return;

    const needPersonal = !this.clientStore.personalDetails();
    const needAddressHistory = !this.clientStore.addressHistory();

    if (!needPersonal && !needAddressHistory) return;

    this._hydratingProfile = true;
    try {
      const tasks: Promise<void>[] = [];
      if (needPersonal) {
        tasks.push(
          firstValueFrom(this.clientService.getPersonalDetails(clientId)).then((details) => {
            this.clientStore.setPersonalDetails(details);
          })
        );
      }

      if (needAddressHistory) {
        tasks.push(
          firstValueFrom(this.clientService.getAddressHistory(clientId)).then((history) => {
            this.clientStore.setAddressHistory(history);
          })
        );
      }

      if (tasks.length) await Promise.all(tasks);
    } catch {
      // Client profile fetching failed
    } finally {
      this._hydratingProfile = false;
    }
  }
}
